<template>
  <div>
    <base-help-tooltip>
      <template slot="left">
        <h3 data-test="campaign-name-label">
          General Information
        </h3>
      </template>
      <template slot="popover-body">
        <p>This section contains essential campaign details that define your advertising effort.</p>
        <p><b>The Campaign Name</b> helps you identify this specific campaign in the Campaign Dashboard and in reporting.</p>
        <p><b>The Advertiser Name</b> represents the brand that this campaign promotes. It's likely to be the same as your company name. Important: Once you proceed beyond this first setup page, you cannot change the Advertiser Name.</p>
        <p><b>For Currency</b>, you can select which currency will be used for all budget allocations. You may change the currency at any point if needed, but please note that your campaign will be billed in the currency you select here.</p>
      </template>
    </base-help-tooltip>
    <b-form-group
      class="mb-4"
      id="general-section"
    >
      <template
        slot="label"
        class="form-label"
      >
        Campaign Name <span class="form-required">*</span>
        <span
          class="form-label-small text-muted"
          data-test="campaign-name--valid-label"
        >
          {{ this.campaignName.length }}/{{ maxFieldLength }}
        </span>
      </template>
      <b-form-input
        id="input-1"
        v-model="campaignName"
        @blur.native="campaignInputOnBlur"
        @input="campaignInputOnChange"
        trim
        data-test="campaign-name"
        :state="campaignNameValid"
        :maxlength="maxFieldLength"
        :readonly="GET_IS_CAMPAIGN_READONLY"
        :disabled="GET_IS_CAMPAIGN_READONLY"
      />
      <b-form-invalid-feedback id="input-1">
        Name is already in use.
      </b-form-invalid-feedback>
    </b-form-group>
    <template v-if="GET_IS_MANAGED_CAMPAIGN">
      <div class="mb-2">
        <b-row>
          <b-col
            lg="6"
            xl="6"
          >
            <b-form-group
              id="account-manager-set"
            >
              <template
                slot="label"
                label-for="account-manager-set"
                class="form-label"
              >
                <span class="form-label input-label-title">Campaign Owner *</span>
              </template>
              <b-form-select
                :disabled="GET_IS_CAMPAIGN_READONLY"
                v-model="selectedAccountManagerId"
                :options="accountManagerList"
                data-test="account-manager-select"
                @change="updateCampaignInfo('accountManagerUserId', selectedAccountManagerId)"
              />
            </b-form-group>
          </b-col>
          <b-col
            lg="6"
            xl="6"
          >
            <b-form-group
              id="advertiser-set"
            >
              <template
                slot="label"
                label-for="advertiser-set"
                class="form-label"
              >
                <span class="form-label input-label-title">Advertiser *
                  <span
                    class="form-label-small text-muted"
                  >
                    <b-button
                      size="sm"
                      v-b-modal.newAdvertiserModal
                      data-test="new-advertiser-button"
                      v-if="isNewCampaign"
                      variant="secondary"
                    >
                      Add Advertiser
                    </b-button>
                  </span>
                </span>
              </template>
              <b-form-select
                :disabled="!isNewCampaign"
                v-model="advertiserSelected"
                :options="getAdvertiserList"
                data-test="campaign-advertiser-select"
                @change="setSelectedAdvertiser"
              />
            </b-form-group>
            <div
              class="alert alert-sm alert-warning"
              data-test="advertiser-alert"
              v-if="isNewCampaign"
            >
              Advertiser name cannot be changed once you exit this page.
            </div>
          </b-col>
          <b-col
            lg="6"
            xl="6"
          >
            <b-form-group
              id="agency-set"
            >
              <template
                slot="label"
                label-for="agency-set"
                class="form-label"
              >
                <span class="form-label">Agency *
                  <span
                    class="form-label-small text-muted"
                  >
                    <b-button
                      size="sm"
                      v-b-modal.newAgencyModal
                      data-test="new-agency-button"
                      variant="secondary"
                      :disabled="GET_IS_CAMPAIGN_READONLY"
                    >
                      Add Agency
                    </b-button>
                  </span>
                </span>
              </template>
              <b-form-select
                v-model="agencySelected"
                :options="getAgencyList"
                data-test="campaign-agency-select"
                :disabled="GET_IS_CAMPAIGN_READONLY"
                @change="setSelectedAgency"
              />
            </b-form-group>
          </b-col>
          <b-col
            lg="6"
            xl="6"
          >
            <b-form-group
              id="campaign-group-set"
            >
              <template
                slot="label"
                label-for="agency-set"
                class="form-label"
              >
                <div class="form-label input-label-title">
                  Campaign Group
                </div>
              </template>
              <base-autocomplete
                id="search-groups"
                class="w-100"
                data-test="campaign-group-name-filter"
                :items="campaignGroups"
                :disabled="GET_IS_CAMPAIGN_READONLY"
                :placeholder="'Search'"
                v-model="campaignGroupSearchName"
                :search-text.sync="campaignGroupSearchName"
                @blur="updateCampaignGroupNameInfo($event.target.value)"
                @keyup.native.enter="updateCampaignGroupName()"
                @keyup.tab="updateCampaignGroupName()"
                @mouseleave="updateCampaignGroupName()"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </div>
      <b-modal
        id="newAdvertiserModal"
        ref="newAdvertisermodal"
        title="Add A New Advertiser"
        @show="resetModal"
        @hidden="resetModal"
        @ok="handleOk"
      >
        <form
          ref="form"
          @submit.stop.prevent="handleSubmit"
        >
          <b-form-group
            label="Advertiser Name"
            label-for="newAdvertiserModal"
          >
            <base-autocomplete
              id="advertiser-name"
              v-model="advertiserName"
              :items="adxAdvertisers"
              :wait-length="3"
            />
          </b-form-group>
        </form>
      </b-modal>
      <b-modal
        id="newAgencyModal"
        ref="newAgencyModal"
        title="Add A New Agency"
        @show="resetAgencyModal"
        @hidden="resetAgencyModal"
        @ok="handleAgencyOk"
      >
        <form
          ref="form"
          @submit.stop.prevent="handleAgencySubmit"
        >
          <b-form-group
            label="Agency Name"
            label-for="newAgencyModal"
          >
            <b-form-input
              id="agency-name"
              v-model="agencyName"
              trim
              data-test="agency-name-input"
            />
          </b-form-group>
        </form>
      </b-modal>
    </template>
    <template v-else>
      <b-form-group
        id="general-information-title-group"
      >
        <p
          class="mb-0"
          data-test="advertiser-name-label"
        >
          Advertiser Name <span class="form-required">*</span>
          <span
            class="form-label-small text-muted"
            data-test="advertiser-name--valid-label"
          >
            {{ advertiserNameLength }}/{{ maxFieldLength }}
          </span>
        </p>
        <base-autocomplete
          id="advertiser-name"
          :items="autoAdvertisersList"
          :value="autoAdvertiserName"
          @input="updateAutoAdvertiserNameLength($event)"
          @blur="updateAutoSelectedAdvertiser($event.target.value)"
          data-test="self-service-advertiser-name"
          :maxlength="maxFieldLength"
          :disabled="!this.GET_IS_NEW_CAMPAIGN"
        />
      </b-form-group>
      <div
        class="alert alert-sm alert-warning mb-4 w-50"
        data-test="advertiser-alert"
        v-if="GET_IS_NEW_CAMPAIGN"
      >
        Advertiser name cannot be changed once you exit this page.
      </div>
      <template>
        <b-form-group
          class="w-50"
          id="general-information-campaign-group"
        >
          <p
            class="mb-0"
            data-test="campaign-group-label"
          >
            Campaign Group
          </p>
          <base-autocomplete
            id="search-groups"
            class="w-100"
            data-test="campaign-group-name-filter"
            :items="campaignGroups"
            :placeholder="'Search'"
            v-model="campaignGroupSearchName"
            :disabled="GET_IS_CAMPAIGN_READONLY"
            :search-text.sync="campaignGroupSearchName"
            @blur="updateCampaignGroupNameInfo($event.target.value)"
            @keyup.native.enter="updateCampaignGroupName()"
            @keyup.tab="updateCampaignGroupName()"
            @mouseleave="updateCampaignGroupName()"
          />
        </b-form-group>
      </template>
    </template>
    <div class="mb-4">
      <b-row
        :class="['gutters-0', { 'new-gutters-sm': GET_IS_MANAGED_CAMPAIGN }]"
      >
        <b-col
          lg="4"
          xl="3"
        >
          <currency-field
            @updateCurrency="updateCurrency"
            :disabled="GET_IS_CAMPAIGN_READONLY"
          />
        </b-col>
      </b-row>
    </div>
  </div>
</template>

<script>
import {
  mapState, mapGetters, mapActions, mapMutations,
} from 'vuex';
import * as campaignTypes from '@/modules/campaigns/store/types';
import * as accountTypes from '@/modules/account/store/types';
import BaseAutocomplete from '@/components/form/BaseAutocomplete.vue';
import BaseHelpTooltip from '@/components/BaseHelpTooltip.vue';
import helper from '@/core/helper';
import CurrencyField from '@/modules/campaigns/components/CampaignInfo/CurrencyField';

export default {
  name: 'GeneralSection',
  components: {
    CurrencyField,
    BaseHelpTooltip,
    BaseAutocomplete,
  },
  data() {
    return {
      maxFieldLength: 128,
      advertiserNameLength: 0,
      advertiserSelected: -1,
      agencySelected: -1,
      selectedAccountManagerId: -1,
      selectedSalesUserId: -1,
      advertiserName: '',
      newAdvertiserName: '',
      agencyName: '',
      newAgencyName: '',
      campaignName: '',
      accountManagerList: [],
      salesList: [],
      agencyList: [],
      campaignNameValid: null,
      selectedAutoAdvertiser: {
        name: '',
      },
      campaignGroupSearchName: null,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'adxAdvertisers',
      'selectedAdvertiser',
      'existingAdvertisers',
      'campaign',
      'campaignGroups',
    ]),
    ...mapGetters('campaigns', [
      campaignTypes.GET_IS_NEW_CAMPAIGN,
      campaignTypes.GET_IS_MANAGED_CAMPAIGN,
      campaignTypes.GET_ADVERTISER_LIST,
      campaignTypes.GET_ACCOUNT_ADVERTISER,
      campaignTypes.GET_IS_CAMPAIGN_READONLY,
    ]),
    existingAdvertisersNames() {
      return this.existingAdvertisers.map((advertiser) => advertiser.name);
    },
    autoAdvertisersList() {
      const adxNoDuplicates = this.adxAdvertisers.filter((advertiser) => !this.existingAdvertisersNames.includes(advertiser.name.trim()));
      return [...this.existingAdvertisers, ...adxNoDuplicates];
    },
    autoAdvertiserName() {
      if (this.GET_IS_MANAGED_CAMPAIGN) return;
      if (this.GET_IS_NEW_CAMPAIGN) {
        this.updateAutoAdvertiserNameLength(this.selectedAdvertiser?.name);
        return this.selectedAdvertiser?.name;
      }
      const campaignAdvertiser = this.existingAdvertisers.find((advertiser) => this.campaign.advertiserId === advertiser.id);
      if (campaignAdvertiser) {
        this.updateAutoAdvertiserNameLength(campaignAdvertiser?.name);
        return campaignAdvertiser.name;
      }
      this.updateAutoAdvertiserNameLength(this.selectedAutoAdvertiser?.name);
      return this.selectedAutoAdvertiser?.name;
    },
    getAdvertiserList() {
      return (this.newAdvertiserName !== '')
        ? [{ value: -1, text: this.newAdvertiserName }, ...this.GET_ADVERTISER_LIST]
        : this.GET_ADVERTISER_LIST;
    },
    getAgencyList() {
      const agencyList = this.agencyList
        .map((item) => ({ value: item.id, text: item.name }));
      return (this.newAgencyName !== '')
        ? [{ value: -1, text: this.newAgencyName }, ...agencyList]
        : agencyList;
    },
    getAdvertiserSet() {
      const advertiserSet = this.getAdvertiserList
        .find((item) => item.value === this.advertiserSelected);
      return {
        id: advertiserSet ? advertiserSet.value : this.advertiserSelected,
        name: advertiserSet ? advertiserSet.text : this.newAdvertiserName,
        accountId: advertiserSet ? advertiserSet.accountId : null,
      };
    },
    getAgencySet() {
      const agencySet = this.getAgencyList
        .find((item) => item.value === this.agencySelected);
      return {
        id: agencySet ? agencySet.value : this.agencySelected,
        name: agencySet ? agencySet.text : this.newAgencyName,
      };
    },
    isNewCampaign() {
      return Boolean(!this.campaign?.id);
    },
  },
  methods: {
    ...mapActions('account', [
      accountTypes.FETCH_ACCOUNT_BY_ID,
      accountTypes.FETCH_ALL_USERS_IN_ROLE,
    ]),
    ...mapActions('campaigns', [
      campaignTypes.GET_CAMPAIGN_BY_NAME,
      campaignTypes.UPDATE_SELECTED_ADVERTISER,
      campaignTypes.FETCH_AGENCIES,
      campaignTypes.FETCH_ADX_ADVERTISERS,
      campaignTypes.FETCH_ADVERTISER,
      campaignTypes.CREATE_ADVERTISER,
      campaignTypes.FETCH_CAMPAIGN_GROUPS,
      campaignTypes.PATCH_CAMPAIGN,
    ]),
    ...mapMutations('account', [
      accountTypes.RESET_INVOICING_DETAILS,
    ]),
    ...mapMutations('campaigns', [
      campaignTypes.SET_CAMPAIGN_GROUPS,
    ]),
    resetModal() {
      this.advertiserName = '';
    },
    handleOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleSubmit();
    },
    handleSubmit() {
      this.newAdvertiserName = this.advertiserName.trim();
      this.advertiserSelected = -1;
      this.$nextTick(() => {
        this.$bvModal.hide('newAdvertiserModal');
        this.setSelectedAdvertiser();
      });
    },
    resetAgencyModal() {
      this.agencyName = '';
    },
    handleAgencyOk(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.handleAgencySubmit();
    },
    campaignInputOnChange() {
      this.$emit('updateCampaignNameValid', false);
    },
    campaignInputOnBlur: helper.debounce(function checkCampaignName(event) {
      if (this.campaign.name === this.campaignName) {
        this.campaignNameValid = null;
        return;
      }
      if (event.target.value) {
        this.GET_CAMPAIGN_BY_NAME(event.target.value)
          .then(({ status }) => {
            this.campaignNameValid = status !== 200;
            this.updateCampaignInfo('name', event.target.value);
            this.$emit('updateCampaignNameValid', this.campaignNameValid);
          });
      }
    }, 500),
    handleAgencySubmit() {
      this.newAgencyName = this.agencyName.trim();
      this.agencySelected = -1;
      this.$nextTick(() => {
        this.$bvModal.hide('newAgencyModal');
        this.setSelectedAgency();
      });
    },
    setSelectedAdvertiser() {
      this.UPDATE_SELECTED_ADVERTISER(this.getAdvertiserSet);
      this.$emit('updateAdvertiser', this.getAdvertiserSet);
      this.FETCH_CAMPAIGN_GROUPS({ advertiserId: this.selectedAdvertiser.id, customOnly: true });
    },
    setSelectedAgency() {
      this.$emit('updateAgency', this.getAgencySet);
    },
    updateCurrency(currencyId) {
      this.$emit('updateCurrency', currencyId);
    },
    getAllUsersFor(role, refList, setMethod) {
      this.FETCH_ALL_USERS_IN_ROLE(role)
        .then((users) => {
          if (!users) return;
          this[refList] = users.userResponses
            .map((user) => ({
              text: `${user.firstName} ${user.lastName}`,
              value: user.id,
            }));
          this[setMethod]();
        });
    },
    updateCampaignInfo(property, selectedOption) {
      if (selectedOption) this.$emit('updateCampaignInfo', property, selectedOption);
    },
    setSelectedAccountManager() {
      if (this.campaign.accountManagerUserId) {
        const selectedAccountManagerItem = this.accountManagerList
          .find((item) => item.value === this.campaign.accountManagerUserId);
        if (selectedAccountManagerItem) this.selectedAccountManagerId = selectedAccountManagerItem.value;
      } else {
        this.selectedAccountManagerId = this.accountManagerList[0]?.value;
        this.updateCampaignInfo('accountManagerUserId', this.selectedAccountManagerId);
      }
    },
    setSelectedSales() {
      if (this.campaign.salesUserId) {
        const selectedSalesItem = this.salesList
          .find((item) => item.value === this.campaign.salesUserId);
        if (selectedSalesItem) this.selectedSalesUserId = selectedSalesItem.value;
      } else {
        this.selectedSalesUserId = this.salesList[0]?.value;
        this.updateCampaignInfo('salesUserId', this.selectedSalesUserId);
      }
    },
    updateAutoAdvertiserNameLength(name = '') {
      this.advertiserNameLength = name.length;
    },
    updateAutoSelectedAdvertiser(name) {
      const trimmedName = name.trim();
      const existingAdvertiser = this.existingAdvertisers.find((advertiser) => advertiser.name === trimmedName);
      if (existingAdvertiser) {
        this.selectedAutoAdvertiser = existingAdvertiser;
        this.UPDATE_SELECTED_ADVERTISER(existingAdvertiser);
        this.FETCH_CAMPAIGN_GROUPS({ advertiserId: existingAdvertiser.id, customOnly: true });
        this.$emit('updateAdvertiser', existingAdvertiser);
        return;
      }
      const selectedAdvertiserPayload = {
        accountId: this.accountId,
        id: -1,
        name: trimmedName,
        type: 'SELF_SERVICE',
      };
      this.selectedAutoAdvertiser = { ...selectedAdvertiserPayload };
      this.UPDATE_SELECTED_ADVERTISER(selectedAdvertiserPayload);
      this.SET_CAMPAIGN_GROUPS([]);
      this.$emit('updateAdvertiser', selectedAdvertiserPayload);
    },
    updateCampaignGroupNameInfo(name) {
      const trimmedName = name.trim();
      this.$emit('updateCampaignInfo', 'group', trimmedName);
    },
    updateCampaignGroupName() {
      if (this.campaign.id) {
        this.PATCH_CAMPAIGN({ group: this.campaignGroupSearchName })
          .then(() => {
            this.campaign.group = this.campaignGroupSearchName;
          });
      }
    },
    fetchAgencies() {
      this.FETCH_AGENCIES()
        .then((agencies) => {
          this.agencyList = agencies;
          const { campaign } = this;
          const defaultAgency = this.getAgencyList.find((item) => item.text === 'NONE');
          if (campaign?.agencyId == null) this.agencySelected = defaultAgency.value;
          else {
            const agencySet = this.getAgencyList.find((item) => item.value === campaign.agencyId) || defaultAgency;
            this.agencySelected = agencySet.value;
          }
        });
    },
    initialiseManagedData() {
      this.fetchAgencies();
      if (this.selectedAdvertiser) this.advertiserSelected = this.selectedAdvertiser.id;
      this.setSelectedAdvertiser();
      this.getAllUsersFor('ACCOUNT_MANAGER', 'accountManagerList', 'setSelectedAccountManager');
      this.getAllUsersFor('SALES', 'salesList', 'setSelectedSales');
    },
    initialiseAutoData() {
      this.FETCH_CAMPAIGN_GROUPS({ advertiserId: this.selectedAdvertiser.id, customOnly: true });
      if (!this.GET_IS_NEW_CAMPAIGN) {
        this.FETCH_ADVERTISER(this.campaign.advertiserId)
          .then((advertiser) => {
            this.selectedAutoAdvertiser = advertiser;
          });
      } else {
        this.FETCH_ADX_ADVERTISERS();
      }
    },
  },
  created() {
    this.campaignName = this.campaign?.name ? this.campaign.name : '';
    this.campaignGroupSearchName = this.campaign?.group ? this.campaign?.group : '';
    if (this.GET_IS_MANAGED_CAMPAIGN) this.initialiseManagedData();
    else this.initialiseAutoData();
  },
};
</script>

<style>

.input-label-title {
  height: 28px;
}

</style>
